import React, { useState, useEffect } from "react";
import { CommonInstallflowFrame } from "./common-install-flow-frame";
import { AlertTriangle, CheckCircle2, Wifi, WifiOff, Thermometer, Zap, Gauge } from "lucide-react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { ApiPath } from "@/api/path";
import { TSMetric } from "@/query/query";
import { LineChartResponse, SeriesDatumName } from "@/components/charts/lineChartResponse";
import { ONE_SECOND } from "@/dateRanges/windowSizes";
import { LAST_1_MINUTE } from "@/dateRanges/dateRanges";
import { Skeleton } from "@/components/ui/skeleton";
import { getLatestSensorKitPeriod, InstallationData } from "@/installation/installation";
import { Card } from "@/components/ui/card";
import SensorInstalledSuccessfullyImage from "@/assets/sensor-installed-successfully.png";
import {
  installFlowInstallationId,
  installFlowLocationName,
  installFlowAssetName,
  installFlowSensorKitSerial,
} from "@/installFlow/storage-keys";
// ─── Types ────────────────────────────────────────────────────────────────────

type PhaseReading = { label: string; value: number | undefined };
type TemperatureReading = { name: string; value: number; unit: string };
type CheckStatus = "ok" | "warning" | "error" | "loading";

// Sentinel value thresholds — TC sends -1000 if open, +1000 if shorted
const TC_OPEN_THRESHOLD  = -500;
const TC_SHORT_THRESHOLD =  500;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractLatestTemperatures(response: LineChartResponse): TemperatureReading[] {
  return response.series
    .map((series) => {
      const latestDatum = response.datums[series.name]?.find(
        (d) => d.name === SeriesDatumName.LATEST
      );
      if (!latestDatum || latestDatum.datum === null) return null;
      return {
        name: series.name,
        value: latestDatum.datum,
        unit: response.legend.unit || "°F",
      };
    })
    .filter((item): item is TemperatureReading => item !== null);
}

function extractLatestValue(response: LineChartResponse, seriesIndex = 0): number | undefined {
  const series = response.series?.[seriesIndex];
  if (!series) return undefined;
  const datum = response.datums[series.name]?.find((d) => d.name === SeriesDatumName.LATEST);
  if (datum?.datum !== undefined && datum.datum !== null) return datum.datum;
  const lastPoint = series.data?.[series.data.length - 1];
  return lastPoint?.[1] ?? undefined;
}

function getTCSentinelStatus(value: number): "open" | "shorted" | "ok" {
  if (value < TC_OPEN_THRESHOLD) return "open";
  if (value > TC_SHORT_THRESHOLD) return "shorted";
  return "ok";
}

// ─── Design tokens ────────────────────────────────────────────────────────────

const ROW_STYLES = {
  ok:      "bg-emerald-700 border-emerald-700",
  warning: "bg-amber-50 border-amber-300",
  error:   "bg-red-600 border-red-600",
  loading: "bg-gray-50 border-gray-200",
} as const;

const ICON_STYLES = {
  ok:      "bg-emerald-600 text-white",
  warning: "bg-amber-100 text-amber-600",
  error:   "bg-red-500 text-white",
  loading: "bg-gray-100 text-gray-400",
} as const;

const LABEL_STYLES = {
  ok:      "text-white",
  warning: "text-gray-900",
  error:   "text-white",
  loading: "text-gray-500",
} as const;

const VALUE_STYLES = {
  ok:      "text-white",
  warning: "text-gray-900",
  error:   "text-white",
  loading: "text-gray-400",
} as const;

const UNIT_STYLES = {
  ok:      "text-emerald-200",
  warning: "text-gray-400",
  error:   "text-red-200",
  loading: "text-gray-300",
} as const;

// ─── Status Pill ──────────────────────────────────────────────────────────────

function StatusPill({ status }: { status: CheckStatus }) {
  const cfg = {
    ok:      { label: "OK",       cls: "bg-emerald-500 text-white border border-emerald-400" },
    warning: { label: "WARNING",  cls: "bg-amber-500 text-white border border-amber-400" },
    error:   { label: "CHECK",    cls: "bg-red-500 text-white border border-red-400" },
    loading: { label: "LOADING…", cls: "bg-gray-200 text-gray-400 border border-gray-200" },
  }[status];

  return (
    <span className={`text-[10px] font-black tracking-widest uppercase px-2.5 py-1 rounded-full leading-none ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

// ─── Check Row ────────────────────────────────────────────────────────────────

function CheckRow({
  icon,
  label,
  status,
  value,
  unit,
  fixMessage,
}: {
  icon: React.ReactNode;
  label: string;
  status: CheckStatus;
  value?: string;
  unit?: string;
  fixMessage?: string;
}) {
  return (
    <div className={`rounded-xl border-2 transition-colors ${ROW_STYLES[status]}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${ICON_STYLES[status]}`}>
          {icon}
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold leading-tight ${LABEL_STYLES[status]}`}>{label}</p>
          {status === "error" && fixMessage && (
            <p className="text-xs text-red-200 font-medium mt-0.5 leading-snug">{fixMessage}</p>
          )}
          {status === "warning" && fixMessage && (
            <p className="text-xs text-amber-700 font-medium mt-0.5 leading-snug">{fixMessage}</p>
          )}
        </div>

        <div className="flex-shrink-0 flex items-center gap-2">
          {value !== undefined && (
            <span className={`text-sm font-bold tabular-nums ${VALUE_STYLES[status]}`}>
              {value}
              {unit && <span className={`text-xs font-normal ml-0.5 ${UNIT_STYLES[status]}`}>{unit}</span>}
            </span>
          )}
          <StatusPill status={status} />
        </div>
      </div>
    </div>
  );
}

// ─── Sub Row ──────────────────────────────────────────────────────────────────

function SubRow({
  label,
  value,
  unit,
  ok,
  fixMessage,
}: {
  label: string;
  value: string | undefined;
  unit?: string;
  ok: boolean;
  fixMessage?: string;
}) {
  return (
    <div className={`rounded-lg border-2 transition-colors ${ok ? "bg-emerald-700 border-emerald-700" : "bg-red-600 border-red-600"}`}>
      <div className="flex items-center justify-between px-4 min-h-[44px]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full flex-shrink-0 bg-white" />
          <span className="text-sm font-semibold text-white">{label}</span>
        </div>
        <span className="text-sm font-bold tabular-nums text-white">
          {value !== undefined ? (
            <>{value}<span className={`text-xs font-normal ml-0.5 ${ok ? "text-emerald-200" : "text-red-200"}`}>{unit}</span></>
          ) : "—"}
        </span>
      </div>
      {!ok && fixMessage && (
        <p className="text-xs text-red-200 font-medium px-4 pb-3 -mt-1 leading-snug">{fixMessage}</p>
      )}
    </div>
  );
}

// ─── Section Label ────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1.5">
      {children}
    </p>
  );
}

// ─── Cellular Check ───────────────────────────────────────────────────────────

function CellularCheck({ hasCellular, isLoading }: { hasCellular: boolean; isLoading: boolean }) {
  return (
    <div>
      <SectionLabel>Connectivity</SectionLabel>
      <CheckRow
        icon={hasCellular || isLoading ? <Wifi size={18} /> : <WifiOff size={18} />}
        label="Cellular signal"
        status={isLoading ? "loading" : hasCellular ? "ok" : "error"}
        fixMessage="No signal — reposition or check antenna connection"
      />
    </div>
  );
}

// ─── Power Check ──────────────────────────────────────────────────────────────

function PowerCheck({
  totalPower,
  phases,
  isLoading,
}: {
  totalPower: number | undefined;
  phases: PhaseReading[];
  isLoading: boolean;
}) {
  const anyPhaseConnected = phases.some((p) => p.value !== undefined);
  const anyPhaseError     = phases.some((p) => p.value === undefined);

  const overallStatus: CheckStatus = isLoading
    ? "loading"
    : phases.length === 0
    ? "loading"
    : !anyPhaseConnected
    ? "error"
    : anyPhaseError
    ? "warning"
    : totalPower === undefined ? 'error' 
    : totalPower <= 0 
    ? "error" : "ok";

  return (
    <div>
      <SectionLabel>Power</SectionLabel>
      <CheckRow
        icon={<Zap size={18} />}
        label="Current clamps"
        status={overallStatus}
        value={!isLoading && totalPower !== undefined ? totalPower.toFixed(0) : undefined}
        unit="W power draw"
        fixMessage={
          !anyPhaseConnected
            ? "No current readings — check all clamp connections before closing up"
            : anyPhaseError
            ? "One or more phases missing — verify all clamps are seated"
            : undefined
        }
      />
      <div className="mt-1.5 flex flex-col gap-1.5">
        {isLoading
          ? ["L1", "L2", "L3"].map((l) => <Skeleton key={l} className="h-11 rounded-lg" />)
          : phases.map((phase) => {
              const ok = phase.value !== undefined;
              return (
                <SubRow
                  key={phase.label}
                  label={phase.label}
                  value={ok ? phase.value!.toFixed(1) : undefined}
                  unit="A"
                  ok={ok}
                  fixMessage={`Check the current clamp connection for ${phase.label}`}
                />
              );
            })}
      </div>
    </div>
  );
}

// ─── Pressure Check ───────────────────────────────────────────────────────────

function PressureCheck({
  value,
  supplyTemp,
  returnTemp,
  isLoading,
}: {
  value: number | undefined;
  supplyTemp: number | undefined;
  returnTemp: number | undefined;
  isLoading: boolean;
}) {
  const status: CheckStatus = isLoading
    ? "loading"
    : value !== undefined
    ? "ok"
    : "error";

  return (
    <div>
      <SectionLabel>Differential Pressure</SectionLabel>
      <CheckRow
        icon={<Gauge size={18} />}
        label="Differential pressure sensor"
        status={status}
        value={!isLoading && value !== undefined ? value.toFixed(1) : undefined}
        unit="Pa"
        fixMessage={status === "error" ? "No reading — check the pressure sensor connection" : undefined}
      />
      <div className="mt-1.5 flex flex-col gap-1.5">
        {isLoading ? (
          <>
            <Skeleton className="h-11 rounded-lg" />
            <Skeleton className="h-11 rounded-lg" />
          </>
        ) : (
          <>
            <SubRow
              label="Supply temperature"
              value={supplyTemp !== undefined ? supplyTemp.toFixed(0) : undefined}
              unit="°F"
              ok={supplyTemp !== undefined}
              fixMessage="No reading — check the supply temperature sensor connection"
            />
            <SubRow
              label="Return temperature"
              value={returnTemp !== undefined ? returnTemp.toFixed(0) : undefined}
              unit="°F"
              ok={returnTemp !== undefined}
              fixMessage="No reading — check the return temperature sensor connection"
            />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Temperature Check ────────────────────────────────────────────────────────

function TemperatureCheck({
  allNames,
  temperatures,
  isLoading,
}: {
  allNames: string[];
  temperatures: TemperatureReading[];
  isLoading: boolean;
}) {
  const total            = allNames.length;
  const missingCount     = total - temperatures.length;
  const sentinelBadCount = temperatures.filter((t) => getTCSentinelStatus(t.value) !== "ok").length;
  const badCount         = missingCount + sentinelBadCount;
  const allGood          = total > 0 && badCount === 0;
  const noneGood         = temperatures.length === 0;

  const overallStatus: CheckStatus =
    isLoading || total === 0
      ? "loading"
      : allGood
      ? "ok"
      : noneGood
      ? "error"
      : "warning";

  const summaryFixMessage = (() => {
    if (isLoading || total === 0) return undefined;
    const parts: string[] = [];
    if (missingCount > 0)     parts.push(`${missingCount} sensor${missingCount > 1 ? "s" : ""} not connected`);
    if (sentinelBadCount > 0) parts.push(`${sentinelBadCount} sensor${sentinelBadCount > 1 ? "s" : ""} reporting a fault`);
    return parts.length > 0 ? parts.join(", ") + " — check connections before closing up" : undefined;
  })();

  return (
    <div>
      <SectionLabel>Temperature Sensors</SectionLabel>
      <CheckRow
        icon={<Thermometer size={18} />}
        label={`Temperature Sensors${!isLoading && total > 0 ? ` (${total - badCount}/${total})` : ""}`}
        status={overallStatus}
        fixMessage={summaryFixMessage}
      />
      <div className="mt-1.5 flex flex-col gap-1.5">
        {isLoading
          ? [1, 2, 3].map((i) => <Skeleton key={i} className="h-11 rounded-lg" />)
          : allNames.map((name, i) => {
              const reading = temperatures.find((t) => t.name === name);
              const label   = `Temperature Sensor ${i + 1}`;

              if (!reading) {
                return (
                  <SubRow key={name} label={label} value={undefined} ok={false}
                    fixMessage="Not connected — check the sensor plug" />
                );
              }

              const sentinel = getTCSentinelStatus(reading.value);

              if (sentinel === "open") {
                return (
                  <SubRow key={name} label={label} value={undefined} ok={false}
                    fixMessage="Sensor open — not attached correctly, check the connection" />
                );
              }

              if (sentinel === "shorted") {
                return (
                  <SubRow key={name} label={label} value={undefined} ok={false}
                    fixMessage="Sensor shorted — check for damaged wiring or connector" />
                );
              }

              return (
                <SubRow key={name} label={label} value={reading.value.toFixed(0)} unit={reading.unit} ok={true} />
              );
            })}
      </div>
    </div>
  );
}

// ─── Confetti ─────────────────────────────────────────────────────────────────

function useConfetti(trigger: boolean) {
  useEffect(() => {
    if (!trigger) return;

    const canvas = document.createElement("canvas");
    canvas.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:9999";
    document.body.appendChild(canvas);
    const ctx = canvas.getContext("2d")!;
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;

    const pieces = Array.from({ length: 120 }, () => ({
      x:     Math.random() * canvas.width,
      y:     Math.random() * -canvas.height,
      w:     6 + Math.random() * 8,
      h:     10 + Math.random() * 6,
      r:     Math.random() * Math.PI * 2,
      dr:    (Math.random() - 0.5) * 0.15,
      dy:    3 + Math.random() * 4,
      dx:    (Math.random() - 0.5) * 2,
      color: ["#3b82f6", "#60a5fa", "#93c5fd", "#1d4ed8", "#bfdbfe"][Math.floor(Math.random() * 5)],
    }));

    let frame: number;
    let alive = true;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach((p) => {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
        p.y += p.dy;
        p.x += p.dx;
        p.r += p.dr;
      });
      if (pieces.some((p) => p.y < canvas.height) && alive) {
        frame = requestAnimationFrame(draw);
      } else {
        canvas.remove();
      }
    };

    frame = requestAnimationFrame(draw);
    return () => { alive = false; cancelAnimationFrame(frame); canvas.remove(); };
  }, [trigger]);
}

// ─── Equipment Card ───────────────────────────────────────────────────────────

function EquipmentCard({
  assetName,
  serialNumber,
  location,
  allGood,
}: {
  assetName: string;
  serialNumber: string;
  location: string;
  allGood: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center w-full gap-2 mt-4">
{allGood && (
  <div className="flex flex-col items-center justify-center w-full gap-2 h-[150px] py-5">
  <img src={SensorInstalledSuccessfullyImage} alt="sensor installed successfully" className="w-36 h-36 pb-6" />
  <h2 className="text-xl font-bold text-black leading-3">Sensor installed successfully</h2>
</div>
) }

    <Card className="p-5 border-2 border-gray-100">
      <div className="flex justify-center mb-3">
        <div className="inline-flex items-center gap-1.5 bg-emerald-700 rounded-full px-4 py-1.5">
          <CheckCircle2 size={12} className="text-white" />
          <span className="text-xs font-bold text-white tracking-wide">Sensor linked</span>
        </div>
      </div>
      <div className="text-center space-y-0.5">
        <p className="text-base font-bold text-gray-900 leading-tight">{assetName || "—"}</p>
        {location && <p className="text-sm text-gray-500">{location}</p>}
        {serialNumber && (
          <p className="text-xs font-mono text-gray-400 pt-0.5">S/N {serialNumber}</p>
        )}
      </div>
    </Card>
    </div>
  );
}

// ─── Readings Section ─────────────────────────────────────────────────────────

function ReadingsSection({
  installation,
  onAllGoodChange,
}: {
  installation: InstallationData;
  onAllGoodChange: (value: boolean) => void;
}) {
  const installationId = installation.id;
  const phaseCount     = installation?.equipment_model?.phase_count;
  const currentMetrics = phaseCount === 1
    ? [TSMetric.PHASE_A_CURRENT]
    : [TSMetric.ALL_PHASE_CURRENTS];

  // ── Grace period — 10 fetch attempts before showing any red states ──────────
  const MAX_ATTEMPTS = 10;
  const [attempts,      setAttempts     ] = useState(0);
  const [hasEverLoaded, setHasEverLoaded] = useState(false);

  const graceOver = attempts >= MAX_ATTEMPTS;
  const progress  = Math.min((attempts / MAX_ATTEMPTS) * 100, 100);

  const latestPeriod = getLatestSensorKitPeriod(installation);
  const isVersion282 = latestPeriod?.sensor_kit.config.version === "2.8.2";

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: temperatureResponse, isLoading: tempLoading, fetchStatus: tempFetchStatus } = useQuery({
    queryKey: ["live-data-temperatures", installationId],
    queryFn: async () => {
      const res = await apiClient.post<LineChartResponse>(
        ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY,
        {
          window_size: ONE_SECOND.value,
          start: LAST_1_MINUTE.value.from().toISOString(),
          stop:  LAST_1_MINUTE.value.to().toISOString(),
          installation_id: [installationId],
          timezone_for_bucketing: Intl.DateTimeFormat().resolvedOptions().timeZone,
          metrics: [TSMetric.ALL_TEMPERATURES],
          target_unit: "degF",
        }
      );
      return res.data;
    },
    refetchInterval: 2000,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });

  // Count each completed fetch attempt (fetching → idle transition) up to MAX_ATTEMPTS
  const prevFetchStatus = React.useRef(tempFetchStatus);
  useEffect(() => {
    if (prevFetchStatus.current === "fetching" && tempFetchStatus === "idle") {
      setAttempts((a) => Math.min(a + 1, MAX_ATTEMPTS));
    }
    prevFetchStatus.current = tempFetchStatus;
  }, [tempFetchStatus]);

  const { data: powerResponse, isLoading: powerLoading } = useQuery({
    queryKey: ["live-data-power", installationId, phaseCount],
    queryFn: async () => {
      const res = await apiClient.post<LineChartResponse>(
        ApiPath.INSTALLATION_REFRIGERANT_CONTINUOUS_ACTIVE_POWER,
        {
          window_size: ONE_SECOND.value,
          start: LAST_1_MINUTE.value.from().toISOString(),
          stop:  LAST_1_MINUTE.value.to().toISOString(),
          installation_id: [installationId],
          timezone_for_bucketing: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      );
      return res.data;
    },
    enabled: phaseCount != null,
    refetchInterval: 2000,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });

  const { data: phaseResponse, isLoading: phaseLoading } = useQuery({
    queryKey: ["live-data-phase", installationId, phaseCount],
    queryFn: async () => {
      const res = await apiClient.post<LineChartResponse>(
        ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY,
        {
          window_size: ONE_SECOND.value,
          start: LAST_1_MINUTE.value.from().toISOString(),
          stop:  LAST_1_MINUTE.value.to().toISOString(),
          installation_id: [installationId],
          timezone_for_bucketing: Intl.DateTimeFormat().resolvedOptions().timeZone,
          metrics: currentMetrics,
        }
      );
      return res.data;
    },
    enabled: phaseCount != null,
    refetchInterval: 2000,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });

  const { data: differentialPressureResponse, isLoading: differentialPressure } = useQuery({
    queryKey: ["live-data-dp", installationId],
    queryFn: async () => {
      const res = await apiClient.post<LineChartResponse>(
        ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY,
        {
          window_size: ONE_SECOND.value,
          start: LAST_1_MINUTE.value.from().toISOString(),
          stop:  LAST_1_MINUTE.value.to().toISOString(),
          installation_id: [installationId],
          timezone_for_bucketing: Intl.DateTimeFormat().resolvedOptions().timeZone,
          metrics: [TSMetric.DIFFERENTIAL_PRESSURE],
        }
      );
      return res.data;
    },
    enabled: isVersion282,
    refetchInterval: 2000,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });

  const { data: dpTemperatureResponse, isLoading: dpTemperatureLoading } = useQuery({
    queryKey: ["live-data-dp-temps", installationId],
    queryFn: async () => {
      const res = await apiClient.post<LineChartResponse>(
        ApiPath.INSTALLATION_GENERIC_LINE_CHART_QUERY,
        {
          window_size: ONE_SECOND.value,
          start: LAST_1_MINUTE.value.from().toISOString(),
          stop:  LAST_1_MINUTE.value.to().toISOString(),
          installation_id: [installationId],
          timezone_for_bucketing: Intl.DateTimeFormat().resolvedOptions().timeZone,
          metrics: [TSMetric.DIFFERENTIAL_PRESSURE_TEMPERATURE_1, TSMetric.DIFFERENTIAL_PRESSURE_TEMPERATURE_2],
          target_unit: "degF",
        }
      );
      return res.data;
    },
    enabled: isVersion282,
    refetchInterval: 2000,
    staleTime: 10_000,
    placeholderData: keepPreviousData,
  });

  // ── Derived ────────────────────────────────────────────────────────────────

  const rawTemperatures = temperatureResponse ? extractLatestTemperatures(temperatureResponse) : [];
  const allRawNames     = temperatureResponse?.series.map((s) => s.name) ?? [];
  const tcNameMap       = new Map(allRawNames.map((name, i) => [name, `Temperature Sensor ${i + 1}`]));
  const allTCNames      = allRawNames.map((name) => tcNameMap.get(name)!);
  const temperatures    = rawTemperatures.map((t) => ({ ...t, name: tcNameMap.get(t.name) ?? t.name }));

  const totalPower = powerResponse ? extractLatestValue(powerResponse, 0) : undefined;

  const dpValue       = differentialPressureResponse ? extractLatestValue(differentialPressureResponse, 0) : undefined;
  const dpSupplyTemp  = dpTemperatureResponse ? extractLatestValue(dpTemperatureResponse, 0) : undefined;
  const dpReturnTemp  = dpTemperatureResponse ? extractLatestValue(dpTemperatureResponse, 1) : undefined;

  const phases: PhaseReading[] = phaseResponse?.series
    ? phaseResponse.series.map((s, i) => ({
        label: s.name,
        value: extractLatestValue(phaseResponse, i),
      }))
    : [];

  const hasCellular = temperatures.length > 0;

  useEffect(() => {
    if (temperatureResponse !== undefined) setHasEverLoaded(true);
  }, [temperatureResponse]);

  const phaseConfigured = phaseCount != null;

  const isInitialLoad =
    !graceOver ||
    (!hasEverLoaded && (
      tempLoading ||
      (phaseConfigured && powerLoading) ||
      (phaseConfigured && phaseLoading)
    ));

  const allTCGood = allTCNames.length > 0 && allTCNames.every((name) => {
    const reading = temperatures.find((t) => t.name === name);
    return reading !== undefined && getTCSentinelStatus(reading.value) === "ok";
  });
  const allPhasesGood = phases.length > 0 && phases.every((p) => p.value !== undefined);
  const dpGood = !isVersion282 || dpValue !== undefined;
  const allGood =
    !isInitialLoad &&
    hasCellular &&
    allTCGood &&
    allPhasesGood &&
    dpGood;

  useConfetti(allGood);

  useEffect(() => {
    onAllGoodChange(allGood);
  }, [allGood, onAllGoodChange]);

  return (
    <div className="space-y-5">

      {/* Grace period progress bar — animates across 10 fetch attempts */}
      {!graceOver && (
        <div className="rounded-xl bg-gray-100 border-2 border-gray-200 px-4 py-3.5 space-y-2.5">
          <div className="flex items-center gap-2">
            <AlertTriangle className="text-gray-400 flex-shrink-0" size={16} />
            <p className="text-xs text-gray-500 font-medium">Connecting to sensor…</p>
            <span className="ml-auto text-xs font-bold tabular-nums text-gray-400">
              {attempts}/{MAX_ATTEMPTS}
            </span>
          </div>
          <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-600 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Waiting banner — grace over, never gotten a response yet */}
      {graceOver && !hasEverLoaded && isInitialLoad && (
        <div className="flex items-center gap-3 rounded-xl bg-gray-100 border-2 border-gray-200 px-4 py-3.5">
          <AlertTriangle className="text-gray-400 flex-shrink-0" size={18} />
          <p className="text-xs text-gray-500 font-medium leading-snug">
            Connecting to sensor — usually under a minute after power-on
          </p>
        </div>
      )}

      {/* All-clear banner */}
      {allGood && (
        <div className="flex items-center gap-3 rounded-xl bg-emerald-700 border-2 border-emerald-700 px-4 py-3.5">
          <CheckCircle2 className="text-white flex-shrink-0" size={20} />
          <div>
            <p className="text-sm font-bold text-white">All connected — safe to close up</p>
            <p className="text-xs text-emerald-200 mt-0.5">Every sensor and clamp is reading</p>
          </div>
        </div>
      )}

      <CellularCheck hasCellular={hasCellular} isLoading={isInitialLoad} />
      <PowerCheck totalPower={totalPower} phases={phases} isLoading={isInitialLoad} />
      <TemperatureCheck allNames={allTCNames} temperatures={temperatures} isLoading={isInitialLoad} />
      {isVersion282 && <PressureCheck value={dpValue} supplyTemp={dpSupplyTemp} returnTemp={dpReturnTemp} isLoading={isInitialLoad} />}

    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface AuthProps extends React.HTMLAttributes<HTMLDivElement> {}

export function LiveData({ className }: AuthProps) {
  // beforeLoad in the route guarantees installFlowInstallationId is present,
  // so we read all session values synchronously to avoid a null-on-first-render flash.
  const installationId = installFlowInstallationId.get() ?? "";
  const location       = installFlowLocationName.get() ?? "";
  const assetName      = installFlowAssetName.get() ?? "";
  const serialNumber   = installFlowSensorKitSerial.get() ?? "";

  const [allGood, setAllGood] = React.useState(false);

  const instId = installationId ? Number(installationId) : null;

  const { data: installation } = useQuery<InstallationData>({
    queryKey: ["live-data-installation", instId],
    queryFn: async () => {
      const res = await apiClient.get<InstallationData>(`installations/${instId}/`);
      return res.data;
    },
    enabled: instId !== null,
    staleTime: 60_000,
  });

  return (
    <CommonInstallflowFrame>
      <div className="flex flex-col w-full max-w-md gap-5">

        <EquipmentCard
          assetName={assetName}
          serialNumber={serialNumber}
          location={location}
          allGood={allGood}
        />

        {installation ? (
          <ReadingsSection installation={installation} onAllGoodChange={setAllGood} />
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl bg-gray-100 border-2 border-gray-200 px-4 py-3.5 space-y-2.5">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-gray-400 flex-shrink-0" size={16} />
                <p className="text-xs text-gray-500 font-medium">Loading installation…</p>
              </div>
            </div>
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
          </div>
        )}

      </div>
    </CommonInstallflowFrame>
  );
}
